// Iter-82 — Frontend A/B experiments framework.
//
// Pulls active experiments from `GET /api/experiments` and deterministically
// assigns the current visitor to a variant based on a stable anonymous ID
// (localStorage). The same visitor always sees the same variant for a
// given experiment key, even across sessions and devices that share the
// same localStorage.
//
// Why deterministic client-side bucketing?
//   - Zero added server latency (no per-request lookup).
//   - Survives ad-blockers that drop /api calls because variants are
//     cached in localStorage as a fallback.
//   - Easy to audit: paste a visitor ID into the console + see the
//     variant the math assigns.
//
// Analytics: every assignment fires `experiment_exposure` once per
// {experiment_key, variant_key} per session — sent to both PostHog and
// GA4 via the dual-dispatcher in lib/analytics.js.
import { useEffect, useState } from "react";
import { API } from "./api";
import { track, trackExperimentView } from "./analytics";

const VISITOR_KEY = "carsavr:visitor-id";
const CACHE_KEY = "carsavr:experiments";
const EXPOSURE_KEY = "carsavr:exp-exposures";

// 32-bit FNV-1a hash → deterministic + cheap. Used to combine visitor
// ID + experiment key into a stable 0–1 bucket float.
function fnv1a(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  // Convert to unsigned 32-bit
  return h >>> 0;
}

function ensureVisitorId() {
  if (typeof window === "undefined") return "ssr";
  let id = window.localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id =
      (window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : `v_${Date.now()}_${Math.random().toString(36).slice(2)}`);
    try {
      window.localStorage.setItem(VISITOR_KEY, id);
    } catch (_) {
      /* localStorage may be disabled — fall back to in-memory only */
    }
  }
  return id;
}

// Pick a variant deterministically from the weights array. Returns the
// chosen variant object (with key/weight/payload).
function pickVariant(visitorId, experiment) {
  const total = experiment.variants.reduce(
    (acc, v) => acc + Math.max(0, v.weight || 0),
    0,
  );
  if (!total) return experiment.variants[0];
  // Normalise hash to [0, total). Combine visitor + experiment.key so
  // different experiments bucket independently even for the same visitor.
  const bucket = fnv1a(`${visitorId}|${experiment.key}`) % total;
  let running = 0;
  for (const v of experiment.variants) {
    running += Math.max(0, v.weight || 0);
    if (bucket < running) return v;
  }
  return experiment.variants[experiment.variants.length - 1];
}

let _cache = null;
let _inflight = null;

async function loadExperiments() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    // Try localStorage first for instant first-paint variant
    let cachedItems = null;
    try {
      const raw =
        typeof window !== "undefined"
          ? window.localStorage.getItem(CACHE_KEY)
          : null;
      if (raw) cachedItems = JSON.parse(raw);
    } catch (_) {
      /* swallow */
    }
    if (cachedItems && Array.isArray(cachedItems)) {
      _cache = cachedItems;
    }
    // Always re-fetch in the background so an admin pause / weight change
    // propagates within one page load.
    try {
      const res = await fetch(`${API}/experiments`, { credentials: "omit" });
      if (res.ok) {
        const data = await res.json();
        const items = Array.isArray(data?.items) ? data.items : [];
        _cache = items;
        try {
          window.localStorage.setItem(CACHE_KEY, JSON.stringify(items));
        } catch (_) {
          /* swallow */
        }
      }
    } catch (_) {
      /* swallow — fall back to whatever's cached */
    }
    return _cache || [];
  })();
  return _inflight;
}

function recordExposure(experimentKey, variantKey) {
  if (typeof window === "undefined") return;
  let exposures = {};
  try {
    exposures = JSON.parse(window.sessionStorage.getItem(EXPOSURE_KEY) || "{}");
  } catch (_) {
    exposures = {};
  }
  const tag = `${experimentKey}:${variantKey}`;
  if (exposures[tag]) return;
  exposures[tag] = 1;
  try {
    window.sessionStorage.setItem(EXPOSURE_KEY, JSON.stringify(exposures));
  } catch (_) {
    /* swallow */
  }
  track("experiment_exposure", {
    experiment_key: experimentKey,
    variant_key: variantKey,
  });
  // Iter-275.BA (Phase 5 #21) — emit `experiment_view` alongside the
  // legacy `experiment_exposure` (kept for back-compat with admin
  // dashboards). The audit-required event name standardises across
  // GA4 + PostHog and pairs cleanly with `experiment_convert`.
  trackExperimentView(experimentKey, variantKey);
}

/**
 * useExperiment(key, fallbackPayload) — React hook returning the
 * currently-assigned variant for an experiment.
 *
 * Returns `{ variant: "control"|"<key>", payload: object, ready: bool }`.
 *
 * Before the public registry resolves (first paint, network failure)
 * `variant === "control"` and `payload === fallbackPayload` so the UI
 * always has something safe to render.
 */
export function useExperiment(key, fallbackPayload = {}) {
  const [state, setState] = useState({
    variant: "control",
    payload: fallbackPayload,
    ready: false,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const items = await loadExperiments();
      if (cancelled) return;
      const exp = items.find((e) => e.key === key);
      if (!exp || !Array.isArray(exp.variants) || exp.variants.length === 0) {
        setState({ variant: "control", payload: fallbackPayload, ready: true });
        return;
      }
      const visitorId = ensureVisitorId();
      const chosen = pickVariant(visitorId, exp);
      recordExposure(key, chosen.key);
      setState({
        variant: chosen.key,
        payload: { ...fallbackPayload, ...(chosen.payload || {}) },
        ready: true,
      });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}

// Test hook: exposed for unit tests / debugging in the browser console
// (carsavrExperiments._pickVariant(...) etc). Not part of the public API.
export const _internals = { fnv1a, pickVariant, loadExperiments };
